import { useCallback, useMemo, useState } from 'react';
import Fuse, { FuseOptionKey } from 'fuse.js';
import { useNavigate } from '@tanstack/react-router';
import { useDebouncedCallback } from 'use-debounce';
import {
  SearchFilter,
  SearchFilterParams,
  SortConfig,
} from '@/types/filterSearch';

interface UseSearchFilterSortProps<T> {
  items: T[];
  searchKeys: (keyof T)[];
  searchThreshold?: number;
  sortConfigs: SortConfig<T>;
  routePath: string;
  defaultSearchParams?: Partial<SearchFilterParams>;
  searchDebounceMs?: number;
}

export const useSearchFilterSort = <T>({
  items,
  searchKeys,
  searchThreshold = 0.1,
  sortConfigs,
  routePath,
  defaultSearchParams = {},
  searchDebounceMs,
}: UseSearchFilterSortProps<T>) => {
  const [filterSearch, setFilterSearch] = useState<SearchFilter | null>(null);
  const navigate = useNavigate();

  const fuse = useMemo(
    () =>
      new Fuse(items || [], {
        keys: searchKeys as FuseOptionKey<T>[],
        threshold: searchThreshold,
        distance: 10000,
      }),
    [items, searchKeys, searchThreshold],
  );

  const searchResult = useMemo(() => {
    if (!filterSearch?.query || searchKeys.length === 0) {
      return items;
    }
    return fuse.search(filterSearch.query).map((result) => result.item);
  }, [fuse, items, filterSearch, searchKeys.length]);

  const handleSearchParams = useCallback(
    ({
      query = filterSearch?.query,
      department = filterSearch?.department?.map((f) => f.id),
      orderPurchaseMethod = filterSearch?.orderPurchaseMethod?.map((f) => f.id),
      orderPurchase = filterSearch?.orderPurchase?.map((f) => f.id),
      state = filterSearch?.state?.map((f) => f.id),
      productClass = filterSearch?.productClass?.map((f) => f.id),
      travellerClass = filterSearch?.travellerClass?.map((f) => f.id),
      sort = filterSearch?.sort,
      direction = filterSearch?.direction,
      status = filterSearch?.status?.map((f) => f.id),
      allpayStatus = filterSearch?.allpayStatus?.map((f) => f.id),
      halfProduct = filterSearch?.halfProduct?.id,
      filter = filterSearch?.filter?.id,
      studentAccess = filterSearch?.studentAccess?.id,
      dateFrom = filterSearch?.dateFrom,
      dateTo = filterSearch?.dateTo,
      adminTypeFilter = filterSearch?.adminTypeFilter?.map((f) => f.id),
      manuallyModified = filterSearch?.manuallyModified?.map((f) => f.id),
      claimState = filterSearch?.claimState?.map((f) => f.id),
    }: Partial<SearchFilterParams>) => {
      const effectiveDefaults = {
        query: undefined,
        department: undefined,
        orderPurchaseMethod: undefined,
        orderPurchase: undefined,
        state: undefined,
        productClass: undefined,
        travellerClass: undefined,
        filter: undefined,
        studentAccess: undefined,
        dateFrom: undefined,
        dateTo: undefined,
        halfProduct: undefined,
        adminTypeFilter: undefined,
        manuallyModified: undefined,
        claimState: undefined,
        ...defaultSearchParams,
      };

      const rawParams: Record<string, string | string[] | undefined> = {
        query,
        department,
        orderPurchaseMethod,
        orderPurchase,
        sort,
        direction,
        status,
        allpayStatus,
        halfProduct,
        productClass,
        travellerClass,
        filter,
        studentAccess,
        dateFrom,
        dateTo,
        state,
        adminTypeFilter,
        manuallyModified,
        claimState,
      };

      const cleanedParams = Object.entries(rawParams).reduce(
        (acc, [key, value]) => {
          const defaultValue =
            effectiveDefaults[key as keyof typeof effectiveDefaults];

          // Handle array values for multiselect filters
          if (Array.isArray(value)) {
            if (
              value.length > 0 &&
              (!Array.isArray(defaultValue) ||
                value.some((v) => v !== defaultValue[0]))
            ) {
              acc[key] = value;
            }
          } else if (
            value !== defaultValue &&
            value !== undefined &&
            value !== ''
          ) {
            acc[key] = value;
          }
          return acc;
        },
        {} as Record<string, string | string[]>,
      );

      navigate({
        to: routePath,
        search:
          Object.keys(cleanedParams).length > 0 ? cleanedParams : undefined,
        replace: false,
      });
    },
    [navigate, filterSearch, routePath, defaultSearchParams],
  );

  const updateSearchQueryParams = useCallback(
    (value: string) => {
      handleSearchParams({ query: value });
    },
    [handleSearchParams],
  );

  const debouncedUpdateSearchQueryParams = useDebouncedCallback(
    updateSearchQueryParams,
    searchDebounceMs ?? 0,
  );

  const handleSearch = useCallback(
    (value: string) => {
      setFilterSearch((prev) => ({
        ...prev!,
        query: value,
      }));

      if (searchDebounceMs) {
        debouncedUpdateSearchQueryParams(value);
        return;
      }

      updateSearchQueryParams(value);
    },
    [
      debouncedUpdateSearchQueryParams,
      searchDebounceMs,
      updateSearchQueryParams,
    ],
  );

  const handleSort = useCallback(
    (sort: string, startingDirection?: 'asc' | 'desc') => {
      const direction =
        filterSearch!.sort === sort
          ? filterSearch?.direction === 'asc'
            ? 'desc'
            : 'asc'
          : startingDirection || filterSearch!.direction;

      setFilterSearch((prev) => ({
        ...prev!,
        sort: sort,
        direction: direction,
      }));

      handleSearchParams({
        sort,
        direction: direction,
      });
    },
    [handleSearchParams, filterSearch],
  );

  const sortedItems = useMemo(() => {
    if (!searchResult) return [];

    const sortKey = filterSearch?.sort as keyof typeof sortConfigs;
    const sortFn = sortConfigs[sortKey];

    if (!sortFn) return searchResult;

    return [...searchResult].sort((a, b) =>
      filterSearch?.direction === 'desc' ? -1 * sortFn(a, b) : sortFn(a, b),
    );
  }, [searchResult, sortConfigs, filterSearch]);

  return {
    filterSearch,
    sortedItems,
    setFilterSearch,
    handleSort,
    handleSearchParams,
    handleSearch,
  };
};
